import React, { useState } from 'react';
import { useChildActivities, useChildActivityScores } from '../hooks/useDashboard';
import { ActivityScoreChart } from './ActivityScoreChart';

export const ActivityScoreView = ({ child, t, formatDate }) => {
  const [selectedActivityId, setSelectedActivityId] = useState(null);

  const { data: activitiesData, isLoading: activitiesLoading } = useChildActivities(child?.id, !!child);
  const { data: scoresData, isLoading: scoresLoading } = useChildActivityScores(
    child?.id,
    selectedActivityId,
    !!selectedActivityId
  );

  if (!child) return null;

  if (activitiesLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  const activities = activitiesData?.activities || [];
  const activitiesWithScores = activities.filter(a => a.has_scores);

  if (activitiesWithScores.length === 0) {
    return (
      <div className="text-center py-5 text-muted" data-testid="no-activity-scores">
        <i className="bi bi-graph-up fs-1 d-block mb-3"></i>
        <p>{t('dashboard.index.children.activity_scores.no_activities')}</p>
        <small>{t('dashboard.index.children.activity_scores.complete_activities_hint')}</small>
      </div>
    );
  }

  return (
    <div>
      {/* Activity Selector */}
      <div className="mb-4">
        <label htmlFor="activitySelect" className="form-label fw-medium">
          {t('dashboard.index.children.activity_scores.select_activity')}
        </label>
        <select
          id="activitySelect"
          className="form-select"
          value={selectedActivityId || ''}
          onChange={(e) => setSelectedActivityId(e.target.value ? Number(e.target.value) : null)}
          data-testid="activity-select"
        >
          <option value="">{t('dashboard.index.children.activity_scores.choose_activity')}</option>
          {activitiesWithScores.map((activity) => (
            <option key={activity.id} value={activity.id}>
              {activity.name}
            </option>
          ))}
        </select>
      </div>

      {/* Chart Area */}
      {selectedActivityId && (
        <div className="border rounded-3 p-3 bg-light" data-testid="activity-score-chart-container">
          {scoresLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t('common.loading')}</span>
              </div>
            </div>
          ) : scoresData ? (
            <ActivityScoreChart
              activity={scoresData.activity}
              courses={scoresData.courses}
              scores={scoresData.scores}
              formatDate={formatDate}
              t={t}
            />
          ) : null}
        </div>
      )}

      {/* Placeholder when no activity selected */}
      {!selectedActivityId && (
        <div className="border rounded-3 p-5 text-center text-muted bg-light">
          <i className="bi bi-bar-chart fs-1 d-block mb-3"></i>
          <p className="mb-0">{t('dashboard.index.children.activity_scores.select_activity_hint')}</p>
        </div>
      )}
    </div>
  );
};
